import { FunctionComponent, useEffect, useState } from "react";
import moment from "moment";
import Pagination from "../../../../ui/new_components/common/pagination";
import LoadingWidget from "../../../../ui/components/loading_spinner";
import { fetchGet } from "../../../../service/api_client";

interface NormalizedLog {
  id: string;
  user: string;
  operation: string;
  log_type: string;
  created_at: number;
  remarks?: string;
  changes: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
}

const MASTER_LOG_NOISE_KEYS = new Set([
  "_id",
  "id",
  "__v",
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "created_by_name",
  "updated_by_name",
  "deleted",
  "deleted_at",
  "deleted_by",
  "deleted_by_name",
]);

const deriveItemLogType = (operation: string) => {
  const op = operation?.toLowerCase();
  if (op.includes("approve")) return "approved";
  if (op.includes("update")) return "updated";
  if (op.includes("create")) return "created";
  return "info";
};

const normalizeItemLogs = (logs: any[]): NormalizedLog[] => {
  return logs.map((log) => {
    const logType = deriveItemLogType(log.operation);

    const changes =
      logType === "approved" || logType === "updated"
        ? Object.entries(log.params || {}).map(([field, value]: any) => ({
            field,
            oldValue: value?.original ?? "-",
            newValue: value?.updated ?? "-",
          }))
        : [];

    return {
      id: log.id || log._id,
      user: log.created_by,
      log_type: logType,
      operation: `${log.created_by} ${log.operation} this item`,
      created_at: log.created_at,
      changes,
    };
  });
};

const normalizeMasterLogs = (logs: any[]): NormalizedLog[] => {
  return logs.map((log) => {
    const oldParams = log.params?.old || {};
    const newParams = log.params?.new || {};

    const changes = Object.keys({ ...oldParams, ...newParams })
      .filter((key) => !MASTER_LOG_NOISE_KEYS.has(key))
      .map((key) => ({
        field: key,
        oldValue: oldParams[key] ?? "-",
        newValue: newParams[key] ?? "-",
      }));

    return {
      id: log.id || log._id,
      user: log.user,
      log_type: log.log_type,
      operation: log.operation,
      created_at: log.created_at,
      remarks: log.remarks,
      changes,
    };
  });
};

interface EntityHistoryProps {
  fetchLogs: (params: { page: number; count: number }) => Promise<any>;
}

export const GetMasterLogsByEntityApi = async ({
  entityType,
  entityId,
  page,
  count,
}: {
  entityType: string;
  entityId: string;
  page: number;
  count: number;
}) => {
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });
  console.log("Entityyyy", entityId, entityType);

  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/master_logs/${entityId}/${entityType}?${query}`;

  const res = await fetchGet(url);
  console.log("Responssseee", res);
  if (!res.success) return res;

  return {
    ...res,
    data: {
      ...res.data,
      logs: normalizeMasterLogs(res.data.logs),
    },
  };
};

export const GetItemLogsByEntityApi = async ({
  item_code,
  page,
  count,
}: {
  item_code: string;
  page: number;
  count: number;
}) => {
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/${item_code}/item-logs?${query}`;

  const res = await fetchGet(url);

  if (!res.success) return res;

  return {
    ...res,
    data: {
      ...res.data,
      logs: normalizeItemLogs(res.data.logs),
    },
  };
};

const renderValue = (value: any) => {
  if (value === null || value === undefined) return "-";

  if (typeof value === "object") {
    const entries = Object.entries(value).filter(
      ([key]) => key !== "_id" && key !== "id",
    );

    if (entries.length === 0) return "-";

    return (
      <div className="space-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="flex gap-2">
            <span className="font-medium text-gray-600">{key}:</span>
            <span className="break-words">
              {typeof val === "object" ? renderValue(val) : String(val)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return String(value);
};

const EntityHistory: FunctionComponent<EntityHistoryProps> = ({
  fetchLogs,
}) => {
  const [logs, setLogs] = useState<NormalizedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);

    fetchLogs({ page, count })
      .then((res) => {
        if (res.success) {
          setLogs(res.data.logs);
          setTotal(res.data.metadata?.[0]?.total || 0);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [page]);

  return (
    <div className="flex flex-col justify-between h-full">
      <div
        id="history_wrap"
        className="overflow-auto p-2 bg-gray-100"
        style={{ height: "88vh" }}
      >
        {logs.length === 0 && <p className="text-center my-10">No History!</p>}

        {logs.map((log) => (
          <div key={log.id} className="mb-2 shadow rounded bg-white p-2">
            <b>{log.user}</b>
            <p className="text-sm mt-1">{log.operation}</p>
            {log.changes.length > 0 && (
              <table className="w-full text-xs mt-3 border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border px-2 py-1 text-left">Field</th>
                    <th className="border px-2 py-1 text-left text-red-600">
                      Old Value
                    </th>
                    <th className="border px-2 py-1 text-left text-green-600">
                      New Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {log.changes.map((c) => (
                    <tr key={c.field}>
                      <td className="border px-2 py-1">{c.field}</td>
                      <td className="border px-2 py-1 bg-red-50">
                        {renderValue(c.oldValue ?? "-")}
                      </td>
                      <td className="border px-2 py-1 bg-green-50">
                        {renderValue(c.newValue ?? "-")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {log.remarks && (
              <p className="text-xs mt-2">
                <span className="font-medium text-gray-600">Remarks: </span>
                {log.remarks}
              </p>
            )}
            <div className="flex justify-end mt-2 text-xs italic">
              {moment(log.created_at).format("lll")}
            </div>
          </div>
        ))}
      </div>

      <Pagination
        page={page}
        total={total}
        count={count}
        itemCount={logs.length}
        onChange={(page: number) => setPage(page)}
      />

      <LoadingWidget loading={loading} />
    </div>
  );
};

export default EntityHistory;
